import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { BaseComponent } from './prop-types/component';
import { BrowserRouter } from 'react-router-dom';
import { Amplify, Hub } from './services/amplifyCompat';
import authConfig from './auth-config';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { connect } from 'react-redux';
import { updateLoginState } from './actions/loginActions';
import theme from './themes';
import { useContentTranslationsContext } from './components/contexts/ContentTranslationsContext';
import { getAllTranslations } from './services/translations';
import { Routing } from './routing/Routing';
import './App.css';

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  // [::1] is the IPv6 localhost address.
  window.location.hostname === '[::1]' ||
  // 127.0.0.1/8 is considered localhost for IPv4.
  window.location.hostname.match(
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
  )
);

const parseRedirectUris = (rawValue = '') =>
  rawValue
    .split(',')
    .map((uri) => uri.trim())
    .filter(Boolean);

const pickRedirectUri = (rawValue) => {
  const candidates = parseRedirectUris(rawValue);
  if (candidates.length === 0) return '';

  const matchedOrigin = candidates.find((candidate) => {
    try {
      return new URL(candidate).origin === window.location.origin;
    } catch {
      return false;
    }
  });

  if (matchedOrigin) return matchedOrigin;

  if (isLocalhost) {
    return candidates.find((candidate) => candidate.includes('localhost')) ?? candidates[0];
  }

  return candidates.find((candidate) => !candidate.includes('localhost')) ?? candidates[0];
};

const updatedAwsConfig = {
  ...authConfig,
  oauth: {
    ...authConfig.oauth,
    redirectSignIn: pickRedirectUri(authConfig?.oauth?.redirectSignIn),
    redirectSignOut: pickRedirectUri(authConfig?.oauth?.redirectSignOut),
  },
};

Amplify.configure(updatedAwsConfig);

function App(props) {
  const { updateLoginState } = props;
  const { setContentTranslations } = useContentTranslationsContext();

  useEffect(() => {
    if (window.isNative) {
      const makeSignin = async (data, identities) => {
        let userInfo = JSON.parse(data);
        const userData = {
          UserAttributes: Object.keys(userInfo.attributes).map(k => ({
            Name: k,
            Value: userInfo.attributes[k]
          })),
          Username: userInfo.username
        }
        userData.UserAttributes.push({
          Name: "identities",
          Value: identities
        })

        userInfo.attributes.identities = identities;
        
        // Native sign-in passes tokens directly from the host app; avoid redirect-based auth here.
        window.localStorage.setItem(
          `CognitoIdentityServiceProvider.${authConfig.aws_user_pools_web_client_id}.${userInfo.username}.idToken`,
          userInfo.signInUserSession.idToken.jwtToken
        );
        window.localStorage.setItem(
          `CognitoIdentityServiceProvider.${authConfig.aws_user_pools_web_client_id}.${userInfo.username}.accessToken`,
          userInfo.signInUserSession.accessToken.jwtToken
        );
        window.localStorage.setItem(
          `CognitoIdentityServiceProvider.${authConfig.aws_user_pools_web_client_id}.${userInfo.username}.refreshToken`,
          userInfo.signInUserSession.refreshToken.token
        );
        window.localStorage.setItem(
          `CognitoIdentityServiceProvider.${authConfig.aws_user_pools_web_client_id}.${userInfo.username}.clockDrift`,
          userInfo.signInUserSession.clockDrift
        );
        window.localStorage.setItem(
          `CognitoIdentityServiceProvider.${authConfig.aws_user_pools_web_client_id}.${userInfo.username}.userData`,
          JSON.stringify(userData)
        );
        window.localStorage.setItem(
          `CognitoIdentityServiceProvider.${authConfig.aws_user_pools_web_client_id}.LastAuthUser`,
          userInfo.username
        );
        window.location.reload();
      }
      window.makeSignin = makeSignin;
      window.cancelSignin = () => {};
    }
  }, [])

  useEffect(() => {
    async function setAuthListener() {
      Hub.listen('auth', (data) => {
        switch (data.payload.event) {
          case 'signedOut':
          case 'signOut':
            updateLoginState('signIn');
            break;
          default:
            break;
        }
      });
    }

    setAuthListener();
  }, [updateLoginState]);

  useEffect(() => {
    const downloadTranslations = async (langCode) => {
      let translations;
      switch (langCode) {
        case 'fr':
          try {
            translations = await getAllTranslations();
          } catch (error) {
            console.error('Failed to load translations:', error);
            translations = [];
          }
          setContentTranslations(translations);
          break;
        default:
          break;
      }
    };

    downloadTranslations('fr').catch((error) => {
      console.error('Error downloading translations:', error);
    });
  }, [setContentTranslations]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <BrowserRouter>
          <div
            style={{
              width: '100%',
              height: '100vh',
              overflow: 'hidden',
            }}
          >
            <Routing />
          </div>
        </BrowserRouter>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

App.propTypes = {
  ...BaseComponent,
  updateLoginState: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  updateLoginState,
};

export default connect(null, mapDispatchToProps)(App);
